/**
 * Landing Page Builder V2 - Constants
 * Descriptive component metadata, pricing badge presets, and AI page templates.
 */

import type { ComponentMeta, ComponentType } from "../../_types";
import type { TemplateMeta, TemplateId } from "../types/template-types";

// ============================================================
// Pricing Badge Presets
// ============================================================

/** Available badge preset options for pricing cards */
export const BADGE_PRESETS = [
  { value: "most-popular", label: "Most Popular", icon: "Star" },
  { value: "best-value", label: "Best Value", icon: "Sparkles" },
  { value: "recommended", label: "Recommended", icon: "ThumbsUp" },
  { value: "new", label: "New", icon: "Zap" },
  { value: "limited", label: "Limited", icon: "Clock" },
  { value: "early-bird", label: "Early Bird", icon: "Sunrise" },
] as const;

/** Type for badge preset value */
export type BadgePresetValue = (typeof BADGE_PRESETS)[number]["value"];

// ============================================================
// AI Templates
// ============================================================

/** Template definitions for the template picker modal */
export const TEMPLATE_DEFINITIONS: Record<TemplateId, TemplateMeta> = {
  "ai-generated": {
    id: "ai-generated",
    label: "AI-Generated",
    description: "Let AI design a custom page layout based on your event details",
    sections: [],
    isFullyAI: true,
    tone: "Professional and versatile. Match tone to the event type inferred from details. Short, punchy sentences.",
  },
  conference: {
    id: "conference",
    label: "Conference",
    description: "Multi-day professional conference with sessions and speakers",
    sections: ["hero", "richText", "schedule", "team", "venue", "faq", "pricing", "textButton"],
    isFullyAI: false,
    tone: "Authoritative and informative. Industry-confident language. Emphasize knowledge-sharing and networking. No hype — let the agenda speak for itself. Direct, factual, active voice.",
  },
  gala: {
    id: "gala",
    label: "Gala / Dinner",
    description: "Elegant evening event with venue showcase and pricing tiers",
    sections: ["hero", "richText", "venue", "carousel", "pricing", "faq", "textButton"],
    isFullyAI: false,
    tone: "Elegant and sophisticated. Evoke exclusivity and occasion. Refined, aspirational language. Think black-tie invitation, not marketing email. Understated, no buzzwords.",
  },
  offsite: {
    id: "offsite",
    label: "Company Offsite",
    description: "Team retreat or corporate offsite with activities, schedule, and venue info",
    sections: ["hero", "richText", "schedule", "activities", "team", "venue", "faq", "textButton"],
    isFullyAI: false,
    tone: "Warm and energizing. Conversational but professional. Emphasize team bonding and the experience. Use 'we' and 'you'. Okay to be playful but not cheesy.",
  },
};

/** Ordered list of template IDs for display in the picker */
export const TEMPLATE_ORDER: TemplateId[] = ["ai-generated", "conference", "gala", "offsite"];

// ============================================================
// Component Metadata
// ============================================================

/** Metadata for each component type — label, description, and type reference */
export const COMPONENT_META: Record<ComponentType, ComponentMeta> = {
  navbar: {
    type: "navbar",
    label: "Navigation Bar",
    description:
      "A fixed bar at the top of your page with your logo, section links, and a call-to-action button. Visitors use this to navigate between sections.",
  },
  hero: {
    type: "hero",
    label: "Hero",
    description: "Large banner with headline, date, location, and call-to-action buttons. Always appears first.",
  },
  countdown: {
    type: "countdown",
    label: "Countdown",
    description: "Full-width band that counts down the days, hours, minutes, and seconds to your event.",
  },
  faq: {
    type: "faq",
    label: "FAQ",
    description: "Frequently asked questions with expandable answers",
  },
  venue: {
    type: "venue",
    label: "Venue",
    description: "Venue details including address, map, and amenities",
  },
  schedule: {
    type: "schedule",
    label: "Schedule",
    description: "Event timeline with sessions, speakers, and times",
  },
  richText: {
    type: "richText",
    label: "Rich Text",
    description: "Formatted text content with headings, lists, and links",
  },
  activities: {
    type: "activities",
    label: "Activities",
    description: "Activity list with descriptions and signup options",
  },
  textButton: {
    type: "textButton",
    label: "Text & Button",
    description: "Text content paired with an action button",
  },
  carousel: {
    type: "carousel",
    label: "Carousel",
    description: "Image slideshow with navigation controls",
  },
  video: {
    type: "video",
    label: "Video",
    description: "Embedded YouTube or Vimeo video player",
  },
  team: {
    type: "team",
    label: "Team",
    description: "Team member grid with photos, roles, and social links",
  },
  pricing: {
    type: "pricing",
    label: "Pricing",
    description: "Pricing cards with features, images, and call-to-action buttons",
  },
  cardGrid: {
    type: "cardGrid",
    label: "Card Grid",
    description: "Grid of cards with icons, titles, and optional links or buttons",
  },
  sponsors: {
    type: "sponsors",
    label: "Sponsors",
    description: "Sponsor logos with names, bios, and optional tier groupings",
  },
  divider: {
    type: "divider",
    label: "Divider",
    description: "Adjustable spacing between sections, with an optional visible line",
  },
  footer: {
    type: "footer",
    label: "Footer",
    description: "Bottom-of-page block with logo, link columns, social icons, and copyright",
  },
};

/** Default prompt shown in an empty inline rich-text field (display placeholder + editor placeholder). */
export const DEFAULT_INLINE_PLACEHOLDER = "Click to add content...";
